import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ChevronDown,
  BookOpen,
  Settings2,
  ArrowRightLeft,
  Palette,
  Calculator,
  X,
} from 'lucide-react';
import { PRESETS, Preset } from '../types';

const AccordionSection = ({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="border-b border-zinc-800/50 pb-6 last:border-0 last:pb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group mb-4 flex w-full items-center justify-between text-zinc-100"
      >
        <div className="flex items-center gap-2">
          <Icon
            className={`h-4 w-4 transition-colors ${
              isOpen
                ? 'text-emerald-500'
                : 'text-zinc-500 group-hover:text-zinc-300'
            }`}
          />
          <h2
            className={`text-sm font-semibold tracking-wider uppercase transition-colors ${
              isOpen
                ? 'text-zinc-100'
                : 'text-zinc-500 group-hover:text-zinc-300'
            }`}
          >
            {title}
          </h2>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-zinc-600 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

interface SidebarProps {
  root: number;
  setRoot: (v: number) => void;
  base: number;
  setBase: (v: number) => void;
  power: number;
  setPower: (v: number) => void;
  weight: number;
  setWeight: (v: number) => void;
  method: 'full' | 'contracted';
  setMethod: (m: 'full' | 'contracted') => void;
  activeLevel: number;
  setActiveLevel: (l: number) => void;
  showPolarity: boolean;
  setShowPolarity: (b: boolean) => void;
  showProp9: boolean;
  setShowProp9: (b: boolean) => void;
  showShiftLines: boolean;
  setShowShiftLines: (b: boolean) => void;
  applyPreset: (p: Preset) => void;
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  root,
  setRoot,
  base,
  setBase,
  power,
  setPower,
  weight,
  setWeight,
  method,
  setMethod,
  activeLevel,
  setActiveLevel,
  showPolarity,
  setShowPolarity,
  showProp9,
  setShowProp9,
  showShiftLines,
  setShowShiftLines,
  applyPreset,
  isOpen,
  onClose,
}) => {
  return (
    <>
      {/* Mobile Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          />
        )}
      </AnimatePresence>

      <aside
        className={`custom-scrollbar fixed inset-y-0 left-0 z-50 w-80 transform overflow-y-auto border-r border-zinc-800/50 bg-zinc-950 p-6 transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:bg-zinc-900/40 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="mb-6 flex items-center justify-between md:hidden">
          <h2 className="font-bold text-white">Settings</h2>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-zinc-500 hover:bg-zinc-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          <AccordionSection
            title="Case Studies"
            icon={BookOpen}
            defaultOpen={false}
          >
            <div className="space-y-2">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => applyPreset(p)}
                  className="group w-full rounded-lg border border-zinc-700/50 bg-zinc-800/30 p-3 text-left transition-all hover:border-emerald-500/50 hover:bg-zinc-800"
                >
                  <div className="text-xs font-bold text-zinc-200 group-hover:text-emerald-400">
                    {p.name}
                  </div>
                  <div className="mt-1 line-clamp-1 text-[10px] text-zinc-500">
                    {p.description}
                  </div>
                </button>
              ))}
            </div>
          </AccordionSection>

          <AccordionSection title="Parameters" icon={Settings2}>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="font-mono text-xs text-zinc-500 uppercase">
                  Root (a)
                </label>
                <input
                  type="number"
                  value={root}
                  onChange={(e) => setRoot(Number(e.target.value))}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-2 text-white transition-colors focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="font-mono text-xs text-zinc-500 uppercase">
                  Base (b)
                </label>
                <input
                  type="number"
                  min="2"
                  max="5"
                  value={base}
                  onChange={(e) => setBase(Math.max(2, Number(e.target.value)))}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-2 text-white transition-colors focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="font-mono text-xs text-zinc-500 uppercase">
                  Power (c)
                </label>
                <input
                  type="range"
                  min="0"
                  max="8"
                  value={power}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setPower(val);
                    if (activeLevel > val) setActiveLevel(val);
                  }}
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between font-mono text-[10px] text-zinc-500">
                  <span>0</span>
                  <span className="font-bold text-emerald-500">{power}</span>
                  <span>8</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-mono text-xs text-zinc-500 uppercase">
                  Weight (w)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(Number(e.target.value))}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-2 text-white transition-colors focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>
          </AccordionSection>

          <AccordionSection title="Methodology" icon={ArrowRightLeft}>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMethod('contracted')}
                className={`rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  method === 'contracted'
                    ? 'bg-emerald-500 text-black'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                Contracted
              </button>
              <button
                onClick={() => setMethod('full')}
                className={`rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                  method === 'full'
                    ? 'bg-emerald-500 text-black'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                }`}
              >
                Full Expansion
              </button>
            </div>
            <p className="mt-3 text-[10px] leading-relaxed text-zinc-500 italic">
              {method === 'contracted'
                ? 'Uses boundary shifts and adjacent summation (Pascal-like).'
                : 'Each element splits into b new elements symmetrically.'}
            </p>
          </AccordionSection>

          <AccordionSection title="Visuals" icon={Palette}>
            <button
              onClick={() => setShowPolarity(!showPolarity)}
              className={`mb-2 flex w-full items-center justify-between rounded-xl border px-4 py-3 transition-all ${
                showPolarity
                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                  : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <span className="text-xs font-medium">Color by Polarity</span>
              <div
                className={`relative h-4 w-8 rounded-full transition-colors ${
                  showPolarity ? 'bg-emerald-500' : 'bg-zinc-700'
                }`}
              >
                <div
                  className={`absolute top-1 h-2 w-2 rounded-full bg-white transition-all ${
                    showPolarity ? 'left-5' : 'left-1'
                  }`}
                />
              </div>
            </button>

            <button
              onClick={() => setShowProp9(!showProp9)}
              className={`mb-2 flex w-full items-center justify-between rounded-xl border px-4 py-3 transition-all ${
                showProp9
                  ? 'border-blue-500/50 bg-blue-500/10 text-blue-400'
                  : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <span className="text-xs font-medium">Prop. 9 Tracker</span>
              <div
                className={`relative h-4 w-8 rounded-full transition-colors ${
                  showProp9 ? 'bg-blue-500' : 'bg-zinc-700'
                }`}
              >
                <div
                  className={`absolute top-1 h-2 w-2 rounded-full bg-white transition-all ${
                    showProp9 ? 'left-5' : 'left-1'
                  }`}
                />
              </div>
            </button>

            <button
              onClick={() => setShowShiftLines(!showShiftLines)}
              className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 transition-all ${
                showShiftLines
                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                  : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
              }`}
            >
              <span className="text-xs font-medium">Shift Connections</span>
              <div
                className={`relative h-4 w-8 rounded-full transition-colors ${
                  showShiftLines ? 'bg-emerald-500' : 'bg-zinc-700'
                }`}
              >
                <div
                  className={`absolute top-1 h-2 w-2 rounded-full bg-white transition-all ${
                    showShiftLines ? 'left-5' : 'left-1'
                  }`}
                />
              </div>
            </button>
          </AccordionSection>

          <section className="pt-4">
            <div className="rounded-xl border border-emerald-500/10 bg-emerald-500/5 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Calculator className="h-4 w-4 text-emerald-500" />
                <span className="text-xs font-bold tracking-wider text-emerald-500 uppercase">
                  Result
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-xs text-zinc-500">
                    {root} × {base}
                    <sup>{power}</sup>
                  </span>
                  <span className="font-mono text-lg font-bold text-white">
                    {(root * Math.pow(base, power)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
